import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAlert } from "../../common/contexts/AlertContext";
import { DateRange } from "react-day-picker";
import { useQuery } from "@tanstack/react-query";
import GenericForm, { FormField } from "../GenericForm";
import Table from "../patientsList/genericTable/Table";
import { organizeDeviceSettings } from "../../common/function/organizeDeviceData";
import { getDeviceSettings, updateDeviceSerialNumber } from "../../common/api/deviceService";
import "./DeviceProfile.scss";
import Loader from "../Loader/Loader";

const sortKeyArray = ["symble", "value", "range", "units"];

const DeviceProfile = () => {
    const location = useLocation();

    const deviceInfo = location.state?.user;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const alert = useAlert();

    const [filters, setFilters] = useState<string[]>([]);
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
        undefined
    );
    const [sortKey, setSortKey] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");

    const { isLoading: isDataFetching, error: fetchError, data: deviceSettingsData } = useQuery({
        queryKey: ["deviceSettings", deviceInfo?.serialNumber],
        queryFn: () =>
            getDeviceSettings(deviceInfo?.serialNumber, sortKey, sortOrder, filters),
        select: (data) => organizeDeviceSettings(data),
    });

    let initialFields: FormField[] = [
        // {
        //     label: "Modal Number",
        //     name: "modelNumber",
        //     type: "text",
        //     initialValue: deviceInfo?.modelNumber || "",
        //     disabled: true,
        // },
        {
            label: "Serial Number",
            name: "serialNumber",
            type: "text",
            initialValue: deviceInfo?.serialNumber || "",
            disabled: true,
        },
        {
            label: "Date created",
            name: "creationDate",
            type: "text",
            initialValue: deviceInfo?.creationDate || "",
            disabled: true,
        },
        {
            label: "Firmware Version",
            name: "firmwareVersionId",
            type: "text",
            initialValue: deviceInfo?.hardwareVersionId|| "",
            disabled: true,
        },
        {
            label: "Hardware Version",
            name: "hardwareVersionId",
            type: "text",
            initialValue: deviceInfo?.softmwareVersionId || "",
            disabled: true,
        },
        {
            label: "Application Version",
            name: "applicationVersion",
            type: "text",
            initialValue: deviceInfo?.applicationVersion || "",
            disabled: true,
        },
        {
            label: "Status",
            name: "status",
            type: "text",
            initialValue: deviceInfo?.status || "",
            disabled: true,
        },
        {
            label: "Related Patient",
            name: "patient",
            type: "text",
            initialValue: `${deviceInfo?.patient?.firstName} ${deviceInfo?.patient?.lastName}`,
            disabled: true,
        },
        {
            label: "Related HCP*",
            name: "relatedHCP",
            type: "text",
            initialValue:
                `${deviceInfo?.relatedHCP?.firstName} ${deviceInfo?.relatedHCP?.lastName}` ||
                "",
            disabled: true,
        },
        {
            label: "Center",
            name: "center",
            type: "text",
            initialValue: deviceInfo?.site.SiteName|| "",
            disabled: true,
        },
        {
            label: "Last Sync",
            name: "lastSync",
            type: "text",
            initialValue: deviceInfo?.lastSync || "",
            disabled: true,
        },
    ];

    const [fields, setFields] = useState<FormField[]>(initialFields);

    const getText = (text: string) => t(`deviceList.${text}`);

   if ( isDataFetching) {
       return <Loader text="Loading..." />;
    }

    if (fetchError) {
        return <div>{getText("Error fetching treatments")}</div>;
    }

    function handleSubmit(): void {
        const serialNumberField = fields.find((field) => field.name === "serialNumber");
    
        if (!serialNumberField) {
            alert("Serial Number field is missing.", "error");
            return;
        }
    
        if (deviceInfo?.status !== "Unassigned") {
            alert("Serial Number can only be updated if the device status is Unassigned.", "error");
            return;
        }
    
        const updatedSerialNumber = serialNumberField.initialValue;
    
        updateDeviceSerialNumber(deviceInfo?.id, updatedSerialNumber)
            .then((response) => {
                if (response.success) {
                    alert("Serial Number updated successfully!", "msg");
                    navigate(-1); // Navigate back after update
                } else {
                    alert(response.message || "Failed to update the Serial Number.", "error");
                }
            })
            .catch((error: any) => {
                console.error("Error updating Serial Number:", error);
                alert("An error occurred while updating the Serial Number.", "error");
            });
    }
    

    return (
        <>
            <GenericForm
                backButtonAction={() => navigate(-1)}
                backButtonText={getText("back")}
                fields={fields}
                title={getText("deviceProfile")}
                //buttonText={getText("Save_changes")}
                //onSubmit={handleSubmit}
            >
                <div className="device-profile-container">
                    <h4 className="device-profile-title">{getText("settings")}</h4>
                    <div className="device-profile-table-container">
                        <div className="device-profile-table-wrapper">
                            <Table
                                rowClick={() => { }}
                                filterObj={{}}
                                setSortOrder={setSortOrder}
                                sortOrder={sortOrder}
                                tableHeadline={"tableHeadline"}
                                trashAction={() => { }}
                                setFilters={() => { }}
                                buttonText={"buttonText"}
                                tableData={deviceSettingsData || []}
                                setSortKey={setSortKey}
                                sortKey={sortKey}
                                sortKeysArray={sortKeyArray.map((value) => {
                                    return { name: getText(`sort.${value}`), val: value };
                                })}
                                showCheckbox={false}
                            />
                        </div>
                    </div>
                </div>
            </GenericForm>
        </>
    );
};

export default DeviceProfile;
